import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '@hooks';
import { utils, hotkeys } from '@ohif/core';
import { getReportTemplatesApi } from '../../../../custom/rtk-apis/reports/getReportTemplates';
import { getDistributionCountsApi, DistributionInfo } from '../../../../custom/rtk-apis/hl7/getDistributionCounts';
import { getPatientNameOverridesApi } from '../../../../custom/rtk-apis/reports/getPatientNameOverrides';
import { STATUS_LABELS } from '../../../../custom/pages/express/DistributionPage';
import { getHospitalId } from '../../../../custom/Utils/authUtils';

import {
  Icon,
  StudyListExpandedRow,
  EmptyStudies,
  TooltipClipboard,
  AboutModal,
  UserPreferences,
  LoadingIndicatorProgress,
  useSessionStorage,
  InvestigationalUseDialog,
  Button,
  ButtonEnums,
  Typography,
  LegacyButton,
} from '@ohif/ui';

// useModal must come from ui-next: App.tsx provides ui-next's ModalProvider, so
// @ohif/ui's useModal resolves against a context that is never mounted and returns null.
import { Icons, useModal } from '@ohif/ui-next';

import { Types } from '@ohif/ui';

import i18n from '@ohif/i18n';
import { Onboarding, ScrollArea } from '@ohif/ui-next';
import { Header, Pagination, SideMenu } from '../../../../custom/components';
import { customStyles } from '../../../../custom/Utils/utils';
import CardiacStudyList from '../../../../custom/pages/studies/CardiacStudyListV2';
import {
  isDicomViewerEnabled,
  isMeasurementEnabled,
} from '../../../../custom/Utils/clinicFeatures';
import './worklistGlam.css';
import { useSelector, useDispatch } from 'react-redux';

const PatientInfoVisibility = Types.PatientInfoVisibility;

const { sortBySeriesDate } = utils;

const { availableLanguages, defaultLanguage, currentLanguage } = i18n;

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
}: withAppTypes) {
  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  const [updatedTableDataSource, setUpdatedTableDataSource] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [updateKey, setUpdateKey] = useState(0);
  const [distributionCounts, setDistributionCounts] = useState<Record<string, DistributionInfo>>({});
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});

  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 101;
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
    // so on systems that do not support unload events a user will NOT be able to alter any existing filter
    // in the URL, load the page and have it apply.
    clearOnUnload: true,
  });
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });
  const [StudyInstanceUIDs, setStudyInstanceUIDs] = useState([]);
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const dataState = useSelector(state => state.getAllReportByStudyInstanceResponse);
  const reportTemplateResp = useSelector(state => state.getReportTemplatesResponse);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};

  const sortedStudies = useMemo(() => {
    if (!canSort) {
      return studies;
    }

    return [...studies].sort((s1, s2) => {
      if (shouldUseDefaultSort) {
        const ascendingSortModifier = -1;
        return _sortStringDates(s1, s2, ascendingSortModifier);
      }

      const s1Prop = s1[sortBy];
      const s2Prop = s2[sortBy];

      if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
        return s1Prop.localeCompare(s2Prop) * sortModifier;
      } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
        return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
      } else if (!s1Prop && s2Prop) {
        return -1 * sortModifier;
      } else if (!s2Prop && s1Prop) {
        return 1 * sortModifier;
      } else if (sortBy === 'studyDate') {
        return _sortStringDates(s1, s2, sortModifier);
      }

      return 0;
    });
  }, [canSort, studies, shouldUseDefaultSort, sortBy, sortModifier]);

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  const [customFilter, setCustomFilter] = useState([]);
  const numOfStudies = studiesTotal;
  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows]);

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }

    if (val.patientName) {
      const temp = val.patientName.trim();
      const convertedName =
        temp.includes(' ') || temp.includes(',') ? temp.replace(/[, ]/, '^') : temp;
      val = { ...val, ['patientName']: convertedName };
    }
    console.log('llllllllllllllll', val);
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
    setTimeout(() => {
      setUpdateKey(prev => prev + 1);
    }, 3000);
  };

  const onPageNumberChange = newPageNumber => {
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage = Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString: Record<string, any> = {};
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'mrn' && currValue) {
        queryString.mrn = currValue;
      } else if (key === 'modalities' && Array.isArray(currValue) && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (key === 'reportStatus' && Array.isArray(currValue) && currValue.length) {
        queryString.reportStatus = currValue.join(',');
      } else if (key === 'reportType' && Array.isArray(currValue) && currValue.length) {
        queryString.reportType = currValue.join(',');
      } else if (key === 'studyMarkings' && currValue) {
        queryString.studyMarkings = currValue;
      } else if (key === 'teachingFile' && currValue) {
        queryString.teachingFile = currValue;
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(101 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  const offset = resultsPerPage * rollingPageNumber;
  const offsetAndTake = offset + resultsPerPage;

  useEffect(() => {
    setUpdatedTableDataSource(tableDataSource);
  }, [sortedStudies]);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      // @ts-ignore
      getReportTemplatesApi({
        search: '',
        templates_per_page: 12,
      })
    );
  }, []);

  useEffect(() => {
    if (reportTemplateResp?.status === 'SUCCESS') {
      setTemplateList(reportTemplateResp?.result);
    }
  }, [reportTemplateResp?.status]);

  useEffect(() => {
    if (sortedStudies.length === 0) {
      setDistributionCounts({});
      return;
    }
    const uids = sortedStudies.map(s => s.studyInstanceUid).filter(Boolean);
    if (uids.length === 0) return;
    dispatch(getDistributionCountsApi({ studyInstanceUIDs: uids }))
      .then(action => {
        if (getDistributionCountsApi.fulfilled.match(action)) {
          setDistributionCounts(action.payload || {});
        }
      })
      .catch(() => setDistributionCounts({}));

  }, [sortedStudies, dispatch]);

  useEffect(() => {
    if (sortedStudies.length === 0) {
      setNameOverrides({});
      return;
    }
    const hospitalId = getHospitalId();
    if (!hospitalId) {
      return;
    }
    const uids = sortedStudies.map(s => s.studyInstanceUid).filter(Boolean);
    if (uids.length === 0) {
      return;
    }
    dispatch(getPatientNameOverridesApi({ studyInstanceUIDs: uids, hospitalId }))
      .then(action => {
        if (getPatientNameOverridesApi.fulfilled.match(action)) {
          setNameOverrides(action.payload?.result || {});
        }
      })
      .catch(() => setNameOverrides({}));
  }, [sortedStudies, dispatch]);

  const handleTemplateSelect = (e, studyData) => {
    if (studyData) {
      const selectedTemplate = templateList?.data?.filter(
        temp => temp?.templateName === e?.target?.value
      );
      const studyDataWithTemp = {
        ...studyData,
        defaultTemp: {
          item: selectedTemplate[0],
          hospitalHeader: templateList?.hospitalHeader,
          hospitalLogo: templateList?.hospitalLogo,
        },
      };
      navigate(`/express/${studyData?.studyInstanceUid}`, { state: studyDataWithTemp });
    }
  };

  const GenerateReportBtn = ({ studyData }) => {
    return (
      <div onClick={e => e.stopPropagation()}>
        <select
          id="modality"
          className="form__select form__select--sm"
          style={customStyles.select}
          onChange={e => handleTemplateSelect(e, studyData)}
          disabled={
            role === 'Medical Receptionist' || role === 'Practice Manager' || role === 'Admin'
          }
        >
          <option value="">Generate Report</option>
          {templateList?.data?.map(template => (
            <option
              key={template?.templateName}
              value={template?.templateName}
            >
              {template?.templateName}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const tableDataSource = sortedStudies
    .map((rawStudy, key) => {
      const overrideName = nameOverrides[rawStudy.studyInstanceUid];
      const study = overrideName ? { ...rawStudy, patientName: overrideName } : rawStudy;
      const rowKey = key + 1;
      const isExpanded = expandedRows.some(k => k === rowKey);
      const {
        studyInstanceUid,
        // accession,
        modalities,
        instances,
        // description,
        mrn,
        patientName,
        date,
        time,
      } = study;
      const studyDate =
        date &&
        moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
        moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'));
      const studyTime =
        time &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
          t('Common:localTimeFormat', 'hh:mm A')
        );

      return {
        dataCY: `studyRow-${studyInstanceUid}`,
        clickableCY: studyInstanceUid,
        studyData: study,
        row: [
          {
            key: 'patientName',
            gridCol: 3,
            content: (() => {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {patientName
                      ? <TooltipClipboard>{patientName}</TooltipClipboard>
                      : <span className="text-gray-700">(Empty)</span>
                    }
                  </span>
                </div>
              );
            })(),
          },
          {
            key: 'studyDate',
            content: (
              <>
                {studyDate && <span className="mr-4">{studyDate}</span>}
                {studyTime && <span>{studyTime}</span>}
              </>
            ),
            title: `${studyDate || ''} ${studyTime || ''}`,
            gridCol: 4,
          },
          {
            key: 'report',
            content: <GenerateReportBtn studyData={study} />,
            title: 'Generate Report',
            gridCol: 2,
          },
          {
            key: 'assignTo',
            content: <span>-</span>,
            title: 'Assigned To',
            gridCol: 3,
          },
          {
            key: 'distribution',
            content: (() => {
              const info = distributionCounts[studyInstanceUid] ?? null;
              if (!info || info.count === 0) {
                return <span>{info ? 'No distributions' : '-'}</span>;
              }
              const formatStatus = (s: string) => STATUS_LABELS[s] || s;
              const formatDate = (iso: string | undefined) => {
                if (!iso) return '';
                const d = new Date(iso);
                return isNaN(d.getTime())
                  ? ''
                  : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
              };
              const first = info.distributions[0];
              const label =
                info.count === 1
                  ? `1 — ${formatStatus(first?.status)}`
                  : `${info.count} distributions`;
              const tooltip = info.distributions
                .map((d, i) => {
                  const parts = [`${i + 1}.`, d.toProviderName || 'Unknown', '—', formatStatus(d.status)];
                  const date = formatDate(d.createdAt);
                  if (date) parts.push('—', date);
                  return parts.join(' ');
                })
                .join('\n');
              return (
                <span
                  style={{ cursor: 'pointer', color: '#4f8ef7' }}
                  title={tooltip}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/distribution/${studyInstanceUid}`, {
                      state: {
                        reportId: study.reportId,
                        studyData: {
                          ...study,
                          patientName: study.patientName,
                          name: study.patientName,
                          patientId: study.mrn ?? study.patientId,
                          studyDate: study.date,
                          studyTime: study.time,
                        },
                      },
                    });
                  }}
                >
                  {label}
                </span>
              );
            })(),
            title: 'Distribution',
            gridCol: 2,
          },
          {
            key: 'reportStatus',
            content: study.reportStatus || 'Draft',
            title: study.reportStatus || 'Draft',
            gridCol: 3,
          },
          {
            key: 'reportType',
            content: study.reportType || '-',
            title: study.reportType || 'Report Type',
            gridCol: 2,
          },
          {
            key: 'Dicom',
            content: (
              <div
                className="flex gap-1"
                onClick={e => e.stopPropagation()}
              >
                {/* Per-clinic feature control (Super Admin → Feature control).
                    Read from the cached flags rather than the hook: this is
                    inside a row-mapping callback, not the component body. */}
                {isDicomViewerEnabled() && (
                  <button
                    type="button"
                    className="border-primary-light hover:bg-primary-dark cursor-pointer rounded-md border bg-transparent py-1 px-2 text-sm text-white"
                    onClick={() => navigate(`/viewer?StudyInstanceUIDs=${studyInstanceUid}`)}
                  >
                    Dicom
                  </button>
                )}
                {isMeasurementEnabled() && (
                  <button
                    type="button"
                    className="cursor-pointer rounded-md border border-teal-400 bg-transparent py-1 px-2 text-sm text-teal-300 hover:bg-teal-900/40"
                    onClick={() => navigate(`/measurement?StudyInstanceUIDs=${studyInstanceUid}`)}
                  >
                    Measure
                  </button>
                )}
              </div>
            ),
            title: 'dicom files',
            gridCol: 2,
          },
        ],
        // Todo: This is actually running for all rows, even if they are
        // not clicked on.
        expandedContent: (
          <StudyListExpandedRow
            seriesTableColumns={{
              // description: t('StudyList:Description'),
              seriesNumber: t('StudyList:Series'),
              modality: t('StudyList:Modality'),
              instances: t('StudyList:Instances'),
            }}
            seriesTableDataSource={
              seriesInStudiesMap.has(studyInstanceUid)
                ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
                    return {
                      // description: s.description || '(empty)',
                      seriesNumber: s.seriesNumber ?? '',
                      modality: s.modality || '',
                      instances: s.numSeriesInstances || '',
                    };
                  })
                : []
            }
          >
            <div className="flex flex-row gap-2">
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  navigate(`/distribution/${studyInstanceUid}`, {
                    state: {
                      reportId: study.reportId,
                      studyData: {
                        ...study,
                        patientName: study.patientName,
                        name: study.patientName,
                        patientId: study.mrn ?? study.patientId,
                        studyDate: study.date,
                        studyTime: study.time,
                      },
                    },
                  });
                }}
                className="border-primary-light cursor-pointer rounded-md border bg-transparent py-2 px-3 text-sm text-white shadow-sm ring-1 transition-colors duration-150 hover:bg-primary-dark focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                Distribution
              </button>
              {(appConfig.groupEnabledModesFirst
                ? appConfig.loadedModes.sort((a, b) => {
                    const isValidA = a.isValidMode({
                      modalities: modalities.replaceAll('/', '\\'),
                      study,
                    }).valid;
                    const isValidB = b.isValidMode({
                      modalities: modalities.replaceAll('/', '\\'),
                      study,
                    }).valid;

                    return isValidB - isValidA;
                  })
                : appConfig.loadedModes
              ).map((mode, i) => {
                if (
                  mode.displayName === 'Microscopy' ||
                  mode.displayName === 'Preclinical 4D' ||
                  mode.displayName === 'Total Metabolic Tumor Volume'
                ) {
                  return null;
                }
                const modalitiesToCheck = modalities.replaceAll('/', '\\');

                const { valid: isValidMode, description: invalidModeDescription } =
                  mode.isValidMode({
                    modalities: modalitiesToCheck,
                    study,
                  });
                // TODO: Modes need a default/target route? We mostly support a single one for now.
                // We should also be using the route path, but currently are not
                // mode.routeName
                // mode.routes[x].path
                // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
                // How do we know which params to pass? Today, it's just StudyInstanceUIDs and configUrl if exists
                const query = new URLSearchParams();
                if (filterValues.configUrl) {
                  query.append('configUrl', filterValues.configUrl);
                }
                query.append('StudyInstanceUIDs', studyInstanceUid);
                return (
                  mode.displayName && (
                    <Link
                      className={isValidMode ? '' : 'cursor-not-allowed'}
                      key={i}
                      to={`${dataPath ? '../../' : ''}${mode.routeName}${
                        dataPath || ''
                      }?${query.toString()}`}
                      onClick={event => {
                        // In case any event bubbles up for an invalid mode, prevent the navigation.
                        // For example, the event bubbles up when the icon embedded in the disabled button is clicked.
                        if (!isValidMode) {
                          event.preventDefault();
                        }
                      }}
                      // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
                    >
                      {/* TODO revisit the completely rounded style of buttons used for launching a mode from the worklist later - for now use LegacyButton*/}
                      <Button
                        type={ButtonEnums.type.primary}
                        size={ButtonEnums.size.medium}
                        disabled={!isValidMode}
                        startIconTooltip={
                          !isValidMode ? (
                            <div className="font-inter flex w-[206px] whitespace-normal text-left text-xs font-normal text-white">
                              {invalidModeDescription}
                            </div>
                          ) : null
                        }
                        startIcon={
                          isValidMode ? (
                            <Icons.LaunchArrow className="!h-[20px] !w-[20px] text-black" />
                          ) : (
                            <Icons.LaunchInfo className="!h-[20px] !w-[20px] text-black" />
                          )
                        }
                        onClick={() => {}}
                        dataCY={`mode-${mode.routeName}-${studyInstanceUid}`}
                        className={isValidMode ? 'text-[13px]' : 'bg-[#222d44] text-[13px]'}
                      >
                        {mode.displayName}
                      </Button>
                    </Link>
                  )
                );
              })}
            </div>
          </StudyListExpandedRow>
        ),
        onClickRow: () =>
          setExpandedRows(s => (isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey])),
        isExpanded,
      };
    })
    .filter(x => {
      // console.log('custom', filterValues);
      if (customFilter.length < 1) {
        return x;
      } else {
        for (let i = 0; i < customFilter.length; i++) {
          if (x.clickableCY == customFilter[i].clickableCY) {
            return x;
          }
        }
      }
    });
  const hasStudies = numOfStudies > 0;
  const versionNumber = process.env.VERSION_NUMBER;
  const commitHash = process.env.COMMIT_HASH;

  const menuOptions = [
    {
      title: t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: t('AboutModal:About OHIF Viewer'),
          contentProps: { versionNumber, commitHash },
          containerDimensions: 'max-w-4xl max-h-4xl',
        }),
    },
    {
      title: t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          title: t('UserPreferencesModal:User preferences'),
          content: UserPreferences,
          contentProps: {
            hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(hotkeyDefaults),
            hotkeyDefinitions,
            onCancel: hide,
            currentLanguage: currentLanguage(),
            availableLanguages,
            defaultLanguage,
            onSubmit: state => {
              if (state.language.value !== currentLanguage().value) {
                i18n.changeLanguage(state.language.value);
              }
              hotkeysManager.setHotkeys(state.hotkeyDefinitions);
              hide();
            },
            onReset: () => hotkeysManager.restoreDefaultBindings(),
            hotkeysModule: hotkeys,
          },
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  const { customizationService } = servicesManager.services;
  const { component: DicomUploadComponent } =
    customizationService.getCustomization('dicomUploadComponent') || {};

  const uploadProps =
    DicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled
      ? {
          title: 'Upload files',
          closeButton: true,
          shouldCloseOnEsc: false,
          shouldCloseOnOverlayClick: false,
          content: () => (
            <DicomUploadComponent
              dataSource={dataSource}
              onComplete={() => {
                hide();
                onRefresh();
              }}
              onStarted={() => {
                show({
                  ...uploadProps,
                  // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
                  closeButton: false,
                });
              }}
            />
          ),
        }
      : undefined;

  const { component: dataSourceConfigurationComponent } =
    customizationService.getCustomization('ohif.dataSourceConfigurationComponent') ?? {};

  return (
    <>
    <section
      className="layoutContainer cw-worklist cw-listpage"
      style={customStyles.layoutBackground}
    >
      <div className="cw-worklist__aurora" aria-hidden="true" />
      <div className="layoutGrid bg-transparent">
        <div className="page page--fullBody">
          <Header />
          <div className="page__body">
            <CardiacStudyList
              studies={sortedStudies.slice(offset, offsetAndTake)}
              numOfStudies={numOfStudies}
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              defaultSortValues={defaultSortValues}
              templateList={templateList}
              distributionCounts={distributionCounts}
              role={role}
              pageNumber={pageNumber}
              resultsPerPage={resultsPerPage}
              onPageNumberChange={onPageNumberChange}
              onResultsPerPageChange={onResultsPerPageChange}
              isLoading={isLoadingData}
            />
          </div>
        </div>
      </div>
    </section>

    </>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  modalities: [],
  reportStatus: [],
  reportType: [],
  studyDate: {
    startDate: null,
    endDate: null,
  },
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
  configUrl: null,
  studyMarkings: '',
  teachingFile: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const newParams = new URLSearchParams();
  for (const [key, value] of params) {
    newParams.set(key.toLowerCase(), value);
  }
  params = newParams;

  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn') || '',
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
    reportStatus: params.get('reportstatus') ? params.get('reportstatus').split(',') : [],
    reportType: params.get('reporttype') ? params.get('reporttype').split(',') : [],
    studyMarkings: params.get('studymarkings') || '',
    teachingFile: params.get('teachingfile') || '',
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // Try to get date from either 'date' or 'studyDate' field
  const s1DateStr = s1.date || (s1.studyDate && s1.studyDate.startDate) || '';
  const s2DateStr = s2.date || (s2.studyDate && s2.studyDate.startDate) || '';
  
  const s1Date = moment(s1DateStr, ['YYYYMMDD', 'YYYY.MM.DD', 'MM-DD-YYYY', 'MMM-DD-YYYY'], true);
  const s2Date = moment(s2DateStr, ['YYYYMMDD', 'YYYY.MM.DD', 'MM-DD-YYYY', 'MMM-DD-YYYY'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.isAfter(s2Date) ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
